<template>
  <div class="start-time">
    <div class="top">
      <div class="startnoworcustom" :class="{ '-conflict': conflictTimes.includes('now') }">
        <div class="nowbuttonwrapper">
          <template v-if="showNow">
            <div class="label f8">{{ $t("Start Time") }}</div>
            <UIButton
              :ghost="true"
              class="now"
              @click="nowButtonAction"
              :class="{ '-active': localBooking._startNow && !showCustom }"
            >{{ $t('Now') }}</UIButton>
            <span class="conflictmessage f7">{{ $t('Conflict') }}</span>
          </template>
        </div>
        <UIButton
          :ghost="true"
          class="custom"
          @click="customButtonAction"
          :class="{ '-active': showCustom }"
        >{{ $t('Custom') }}</UIButton>
      </div>
      <StartTimeOtherTimes
        :otherTimes="otherTimes"
        :conflictTimes="conflictTimes"
        :currentTime="currentTime"
        :otherTimesNumber="otherTimesNumber"
        :booking="localBooking"
        :showNow="showNow"
        :showCustom="showCustom"
        :twentyFourHourTime="location.twentyFourHourTime"
        :startTime="startTime"

        @setOtherTime="setOtherTime"
        @update:otherTimes="onOtherTimesUpdate"
        v-on="$listeners"
      />
    </div>
    <div class="customstart" v-if="showCustom">
      <UITimepicker
        :value="timePickerInitialValue"
        @change="setCustomTime"
        :title="$t('Custom Start Time')"
        :twentyFourHourTime="location.twentyFourHourTime"
        :locale="location.locale"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment-timezone';
import DateHelper from '@/core/shared/helpers/DateHelper';
import conflictDetectorWorker from '@/location/conflict-detector.worker';
import UITimepicker from '@/core/shared/components/ui/UITimepicker';
import UIButton from '@/core/shared/components/ui/UIButton';
import StartTimeOtherTimes from './StartTimeOtherTimes';
import { isEqual } from 'lodash-es';

const conflictDetector = conflictDetectorWorker();

@Component({
  components: {
    UITimepicker,
    UIButton,
    StartTimeOtherTimes,
  },
  props: {
    booking: {
      type: Object,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
    currentTime: {
      type: String,
      required: true,
    },
    bookings: {
      type: Array,
      required: true,
    },
    otherTimesNumber: {
      type: Number,
      required: true,
    },
    startTime: {
      type: String,
      required: true,
    },
  },
  watch: {
    'booking.bookingStationTimes': {
      handler: 'generateConflictTimes',
      deep: true,
    },
  },
})
export default class StartTime extends Vue {
  otherTimes = [];
  conflictTimes = [];
  showCustom = this.localBooking._id !== 'new';
  timePickerInitialValue = this.startTime;

  get localBooking () {
    return new Proxy(this.booking, {
      set: (object, property, value) => {
        this.$emit('edit:booking', property, value);
        return true;
      },
    });
  }

  get showNow () {
    return DateHelper.dateMatch(this.startTime, this.currentTime);
  }

  async generateConflictTimes () {
    const mockTimes = [...this.otherTimes];
    if (this.showNow) mockTimes.unshift('now');

    const newConflictTimes = [];
    for (let index = 0; index < mockTimes.length; index++) {
      const time = mockTimes[index];
      const startTime = (time === 'now') ? this.currentTime : time;

      for (let index = 0; index < this.booking.bookingStationTimes.length; index++) {
        const bookingStationTime = this.booking.bookingStationTimes[index];
        const wouldCauseConflict = await conflictDetector.willStartTimeStationAndTierCauseConflict(startTime, { ...bookingStationTime.station }, bookingStationTime.tier, this.booking._id, this.bookings);
        if (wouldCauseConflict) {
          newConflictTimes.push(time);
          break;
        }
      }
    }

    if (!isEqual(this.conflictTimes, newConflictTimes)) this.conflictTimes = newConflictTimes;
  }

  onOtherTimesUpdate (otherTimes) {
    this.otherTimes = otherTimes;
    this.generateConflictTimes();
  }

  customButtonAction (time) {
    this.showCustom = true;
    this.$emit('setStartingTime', this.startTime);
  }

  nowButtonAction () {
    this.showCustom = false;
    this.$emit('setStartingTimeNow');
  }

  setOtherTime (time) {
    this.showCustom = false;
    this.$emit('setStartingTime', time);
  }

  setCustomTime (time) {
    const timePickrTime = moment(time);
    const timePickrTimeWithBookingDate = moment(this.startTime).set({
      hours: timePickrTime.format('HH'),
      minutes: timePickrTime.format('mm'),
      seconds: timePickrTime.format('ss'),
    }).format();

    this.$emit('setStartingTime', timePickrTimeWithBookingDate);
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.start-time {
  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .top {
    margin: 0;
    display: grid;
    grid-gap: var(--spacingMd);

    @media (--tablet) {
      grid-template-columns: 1fr 3fr;
    }

    & .conflictmessage {
      position: absolute;
      bottom: -2rem;
      display: block;
      opacity: 0;
      padding-top: 0.2rem;
      color: var(--colorErrorFlat);
      transition: opacity 0.1s ease;
      text-align: center;
      font-style: italic;
      left: 50%;
      transform: translateX(-50%);
    }

    & .-conflict {
      & .conflictmessage {
        opacity: 1;
      }
    }

    & .startnoworcustom {
      & .nowbuttonwrapper {
        position: relative;
      }

      & .now,
      & .custom {
        display: block;
        width: 100%;
        max-width: 100%;
        padding: calc(var(--spacingSm) - 0.4rem);
      }

      & .now {
        margin-bottom: 3.8rem;
      }

      & .custom {
        margin-top: 0.3rem;
      }
    }
  }

  & .customstart {
    max-width: 21.5rem;
  }
}

</style>
